<template>
  <div :class="'modal-content' + (pinTop ? ' campaign-pin-top' : '')">
    <div class="modal-header">
      <h4 class="modal-title">{{ campaignTitle }}</h4>
      <button type="button" class="close" :aria-label="_('Close')" @click="hideModal"><span aria-hidden="true">&times;</span></button>
    </div>
    <div class="campaign-switcher" v-if="visibleItems.length > 1">
      <button type="button" class="btn btn-transparent btn-sm" :disabled="index === 0" :title="_('Previous')" @click="go(index - 1)"><i class="fa fa-fw fa-chevron-left"></i></button>
      <div class="campaign-switcher-tabs">
        <a v-for="(item, i) in visibleItems" :key="item.key || item.id" :class="'campaign-switcher-tab' + (i === index ? ' active' : '') + (item.dismissed ? ' dismissed' : '')" :title="item.dismissed ? _('Hidden') : ''" @click="go(i)">{{ item.title || _('Check this out!') }}</a>
      </div>
      <span class="campaign-switcher-count">{{ index + 1 }} / {{ visibleItems.length }}</span>
      <button type="button" class="btn btn-transparent btn-sm" :disabled="index >= visibleItems.length - 1" :title="_('Next')" @click="go(index + 1)"><i class="fa fa-fw fa-chevron-right"></i></button>
    </div>
    <div class="modal-body">
      <img v-if="imageUrl" :src="imageUrl" class="img-responsive campaign-image" @load="imgLoaded"/>
      <time v-if="publishedDateText" class="campaign-date">{{ publishedDateText }}</time>
      <div v-html="campaignText" />
    </div>
    <div class="modal-footer campaign-footer">
      <button v-if="showDismiss" type="button" class="btn btn-default" :disabled="dismissDisabled" @click="dismissCampaign">{{ _('Do not show again') }}</button>
      <span v-else></span>
      <a v-if="cta" class="btn btn-primary" @click="runCta" v-html="ctaTitle"></a>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
import { johkuScaledImageSrc } from '~/lib/tiptap/johkuEmbedImage'

export default {
  props: {
    items: {
      type: Array,
      default: () => []
    },
    startIndex: {
      type: Number,
      default: 0
    },
    shopId: {
      type: String,
      default: ''
    },
    preview: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      index: 0,
      pinTop: false,
      dismissedKeys: []
    }
  },
  computed: {
    ...mapGetters(['timezone']),
    visibleItems () {
      return (this.items || []).map(item => this.dismissedKeys.includes(this.itemKey(item)) ? Object.assign({}, item, { dismissed: true }) : item)
    },
    campaign () {
      return this.visibleItems[this.index] || null
    },
    campaignTitle () {
      return (this.campaign && this.campaign.title) || this._('Check this out!')
    },
    campaignText () {
      return (this.campaign && this.campaign.text) || ''
    },
    imageUrl () {
      return johkuScaledImageSrc((this.campaign && this.campaign.image_url) || '')
    },
    publishedDateText () {
      const value = this.campaign && this.campaign.published_date
      if (!value) {
        return ''
      }
      const date = DateTime.fromSQL(String(value).replace('T', ' '), { zone: this.timezone || 'Europe/Helsinki' })
      return date.isValid ? date.toFormat('d.M.yyyy') : ''
    },
    showDismiss () {
      if (!this.campaign) {
        return false
      }
      if (this.preview || this.campaign.preview) {
        return true
      }
      return !this.campaign.dismissed
    },
    dismissDisabled () {
      if (!this.campaign) {
        return true
      }
      if (this.preview || this.campaign.preview) {
        return !parseInt(this.campaign.published, 10)
      }
      return false
    },
    cta () {
      return this.campaign && this.campaign.cta && this.campaign.cta.url
        ? this.campaign.cta
        : null
    },
    ctaTitle () {
      const title = this.cta && this.cta.title
        ? DOMPurify.sanitize(String(this.cta.title), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
        : ''
      return (title || this._('Read more')) + ' <i class="fa fa-fw fa-square-arrow-up-right"></i>'
    }
  },
  methods: {
    itemKey (item) {
      return item ? (item.key || String(item.id)) : ''
    },
    go (i) {
      if (i >= 0 && i < this.visibleItems.length) {
        this.index = i
      }
    },
    hideModal () {
      this.$emit('close')
    },
    imgLoaded () {
      if (window) {
        setTimeout(function () {
          window.dispatchEvent(new Event('resize'))
        }, 500)
      }
    },
    dismissCampaign () {
      const item = this.campaign
      if (!item || this.dismissDisabled) {
        return
      }
      this.EventBus.$emit('campaignDismissed', item)
      this.dismissedKeys.push(this.itemKey(item))
      const next = this.visibleItems.findIndex((candidate, i) => i > this.index && !candidate.dismissed)
      const previous = this.visibleItems.findIndex(candidate => !candidate.dismissed)
      if (next !== -1) {
        this.index = next
      } else if (previous !== -1) {
        this.index = previous
      } else {
        this.$emit('close')
      }
    },
    runCta () {
      if (!this.cta || !this.cta.url) {
        return
      }
      window.open(this.cta.url)
      this.$emit('close')
    }
  },
  created () {
    this.pinTop = (this.items || []).length > 1
    this.index = Math.min(Math.max(this.startIndex, 0), Math.max(this.visibleItems.length - 1, 0))
  }
}
</script>

<style scoped>
.campaign-image {
    max-width: calc(100% + 30px);
    margin: -15px;
    margin-bottom: 15px;
    background-color: #ebeff1;
}
.campaign-date {
    display: block;
    margin-bottom: 8px;
    color: #6b7780;
    font-size: 13px;
}
.campaign-switcher {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-bottom: 1px solid #e5e9ec;
}
.campaign-switcher-tabs {
    display: flex;
    flex: 1;
    gap: 4px;
    overflow-x: auto;
}
.campaign-switcher-tab {
    flex: 0 1 auto;
    max-width: 180px;
    padding: 3px 8px;
    border-radius: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    color: inherit;
    text-decoration: none;
}
.campaign-switcher-tab.active {
    background-color: #ebeff1;
    font-weight: bold;
}
.campaign-switcher-tab.dismissed {
    opacity: 0.55;
}
.campaign-switcher-count {
    white-space: nowrap;
    color: #6b7780;
    font-size: 12px;
}
.campaign-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>

<style>
/* App modals are flex-centered. Several campaigns stay a fixed distance from the top
   so switching or dismissing one does not jump the popup. A single campaign stays centered. */
.vm--container:not(:has(:is(.nav-tabs, .editor-tabs))):has(.campaign-pin-top) {
  justify-content: flex-start;
}
.vm--container:not(:has(:is(.nav-tabs, .editor-tabs))):has(.campaign-pin-top) .vm--modal {
  margin-top: 80px !important;
}
</style>
